import React, { useEffect, useState } from 'react';
import { RgbaColorPicker } from 'react-colorful';
import './ColorPickerInput.css';

const hexToRgba = (hex: string): { r: number; g: number; b: number; a: number } => {
  let r = 0,
    g = 0,
    b = 0,
    a = 1;
  if (hex.length === 7) {
    // #RRGGBB
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  } else if (hex.length === 9) {
    // #RRGGBBAA
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
    a = parseInt(hex.slice(7, 9), 16) / 255; // Normalize alpha
  }
  return { r, g, b, a };
};

const rgbaToHex = ({ r, g, b, a }: { r: number; g: number; b: number; a: number }): string => {
  const toHex = (num: number) => num.toString(16).padStart(2, '0');
  const alphaHex = toHex(Math.round(a * 255));
  return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
};

const isValidHex = (value: string): boolean => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(value);
const isValidRgba = (value: string): boolean =>
  /^rgba?\(\s*\d{1,3},\s*\d{1,3},\s*\d{1,3},?\s*(0|1|0?\.\d+)?\s*\)$/.test(value);

const parseRgbaString = (
  rgbaString: string,
): { r: number; g: number; b: number; a: number } | null => {
  const rgbaMatch = rgbaString.match(/(\d+),\s*(\d+),\s*(\d+),?\s*(0|1|0?\.\d+)?/);
  if (!rgbaMatch) return null;
  return {
    r: parseInt(rgbaMatch[1]),
    g: parseInt(rgbaMatch[2]),
    b: parseInt(rgbaMatch[3]),
    a: rgbaMatch[4] !== undefined ? Math.max(0, Math.min(1, parseFloat(rgbaMatch[4]))) : 1,
  };
};

interface ColorPickerInputProps {
  defaultColor?: string;
  onChange?: (color: string) => void;
}

const ColorPickerInput = ({ defaultColor, onChange }: ColorPickerInputProps) => {
  // Determine initial color from defaultColor prop if provided, else use a default value
  let initialRgba = { r: 170, g: 187, b: 204, a: 1 };
  if (defaultColor) {
    if (defaultColor.startsWith('#') && (defaultColor.length === 7 || defaultColor.length === 9)) {
      initialRgba = hexToRgba(defaultColor);
    } else if (defaultColor.startsWith('rgb')) {
      const parsed = parseRgbaString(defaultColor);
      if (parsed) {
        initialRgba = parsed;
      }
    }
  }

  const [rgbaColor, setRgbaColor] = useState<{ r: number; g: number; b: number; a: number }>(
    initialRgba,
  );
  const [colorFormat, setColorFormat] = useState<'hex' | 'rgba'>('hex'); // can be "hex" or "rgba"
  const [inputValue, setInputValue] = useState<string>(
    colorFormat === 'hex'
      ? rgbaToHex(initialRgba)
      : `rgba(${initialRgba.r}, ${initialRgba.g}, ${initialRgba.b}, ${initialRgba.a.toFixed(2)})`,
  );

  useEffect(() => {
    if (colorFormat === 'hex') {
      setInputValue(rgbaToHex(rgbaColor));
    } else {
      setInputValue(
        `rgba(${rgbaColor.r}, ${rgbaColor.g}, ${rgbaColor.b}, ${rgbaColor.a.toFixed(2)})`,
      );
    }
  }, [colorFormat, rgbaColor]);

  const handleColorChange = (color: { r: number; g: number; b: number; a: number }) => {
    setRgbaColor(color);
    onChange && onChange(rgbaToHex(color));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setInputValue(value);

    if (colorFormat === 'hex' && isValidHex(value)) {
      setRgbaColor(hexToRgba(value));
      onChange && onChange(value);
    } else if (colorFormat === 'rgba' && isValidRgba(value)) {
      const parsedRgba = parseRgbaString(value);
      if (parsedRgba) {
        setRgbaColor(parsedRgba);
        onChange && onChange(value);
      }
    }
  };

  const toggleFormat = () => {
    setColorFormat(colorFormat === 'hex' ? 'rgba' : 'hex');
  };

  // Compute the CSS color for the preview (using rgba format)
  const previewColor = `rgba(${rgbaColor.r}, ${rgbaColor.g}, ${rgbaColor.b}, ${rgbaColor.a})`;

  return (
    <div className="color-picker-container">
      <div className="picker-and-preview">
        <div className="picker">
          <RgbaColorPicker color={rgbaColor} onChange={handleColorChange} />
        </div>
        <div
          className="color-preview"
          style={{
            backgroundColor: previewColor,
          }}
        />
      </div>

      <div className="input-section">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={colorFormat === 'hex' ? '#RRGGBBAA' : 'rgba(R, G, B, A)'}
        />
        <button onClick={toggleFormat}>{colorFormat === 'hex' ? 'RGBA' : 'HEX'}</button>
      </div>
    </div>
  );
};

export default ColorPickerInput;
